import React, { useEffect, Suspense } from 'react';
import { Routes, Route, useLocation, Outlet, useNavigationType } from 'react-router-dom';
import Header from './components/Header';
import Footer from './components/Footer';

// Lazy Load Pages
const HomePage = React.lazy(() => import('./pages/HomePage'));
const AboutPage = React.lazy(() => import('./pages/AboutPage'));
const ProductPage = React.lazy(() => import('./pages/ProductPage'));
const ProductDetailsPage = React.lazy(() => import('./pages/ProductDetailsPage'));
const ResourcesPage = React.lazy(() => import('./pages/ResourcesPage'));
const ContactPage = React.lazy(() => import('./pages/ContactPage'));
const NotFoundPage = React.lazy(() => import('./pages/NotFoundPage'));
const NapoleonLandingPage = React.lazy(() => import('./pages/NapoleonLandingPage'));
const PrivacyPolicyPage = React.lazy(() => import('./pages/PrivacyPolicyPage'));
const TermsAndConditionsPage = React.lazy(() => import('./pages/TermsAndConditionsPage'));

const ScrollToTop = () => {
  const { pathname } = useLocation();
  const navType = useNavigationType();

  useEffect(() => {
    if (navType !== 'POP') {
      window.scrollTo(0, 0);
    }
  }, [pathname, navType]);

  return null;
};

const LoadingSpinner = () => (
  <div className="min-h-screen flex items-center justify-center pt-[72px]">
    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
  </div>
);

const MainLayout = () => {
  return (
    <>
      <Header />
      <main id="main-content">
        <Suspense fallback={<LoadingSpinner />}>
          <Outlet />
        </Suspense>
      </main>
      <Footer />
    </>
  );
};

const App = () => {
  // Global Click Tracking for GTM
  useEffect(() => {
    const handleGlobalClick = (e) => {
      const link = e.target.closest('a');
      if (!link) return;

      const href = link.getAttribute('href');
      if (!href) return;

      if (href.startsWith('tel:')) {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          event: 'phone_click',
          phoneNumber: href.replace('tel:', '')
        });
      }
      
      if (href.startsWith('mailto:')) {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          event: 'email_click',
          emailAddress: href.replace('mailto:', '')
        });
      }
    };

    document.addEventListener('click', handleGlobalClick);
    return () => document.removeEventListener('click', handleGlobalClick);
  }, []);

  return (
    <div className='relative w-full min-h-screen bg-white text-gray-900 font-sans selection:bg-primary'>
      <ScrollToTop />

      <Routes>
        {/* Wrap Pages WITH Header/Footer in the Layout */}
        <Route element={<MainLayout />}>
          <Route path="/" element={<HomePage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/products" element={<ProductPage />} />
          <Route path="/products/:slug" element={<ProductDetailsPage />} />
          <Route path="/resources" element={<ResourcesPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/napoleon" element={<NapoleonLandingPage />} />
          <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
          <Route path="/terms-and-conditions" element={<TermsAndConditionsPage />} />
        </Route>

        {/* Leave 404 Page OUTSIDE the Layout (No Header/Footer) */}
        <Route path="*" element={
          <Suspense fallback={<LoadingSpinner />}>
            <NotFoundPage />
          </Suspense>
        } />
      </Routes>
    </div>
  );
};

export default App;